import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaClipboardList, FaPlus, FaSearch, FaList } from 'react-icons/fa';
import { Link } from 'react-router-dom';

interface Term {
  id: number;
  name: string;
  is_active: boolean;
  academic_year_name: string;
}

interface TermAssessment {
  id: number;
  term_name: string;
  assessment_type_name: string;
  max_marks: number;
  weight: number;
}

const ShowExams: React.FC = () => {
  const [assessments, setAssessments] = useState<TermAssessment[]>([]);
  const [filteredAssessments, setFilteredAssessments] = useState<TermAssessment[]>([]);
  const [activeTerm, setActiveTerm] = useState<Term | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const init = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          window.location.href = '/';
          return;
        }
        dispatch(setAuth(true));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Fetch Active Term
        const termsRes = await axios.get('/exams/terms/');
        const terms = termsRes.data;
        const active = terms.find((t: Term) => t.is_active);

        if (active) {
          setActiveTerm(active);
          // Fetch Assessments for Active Term
          const assessRes = await axios.get(`/exams/term-assessments/?term_id=${active.id}`);
          setAssessments(assessRes.data);
          setFilteredAssessments(assessRes.data);
        } else {
          // Fallback: Fetch all assessments if no active term? Or just empty
          // Or fetch first term found
          if (terms.length > 0) {
            const first = terms[0];
            setActiveTerm(first);
            const assessRes = await axios.get(`/exams/term-assessments/?term_id=${first.id}`);
            setAssessments(assessRes.data);
            setFilteredAssessments(assessRes.data);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch exam data');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [dispatch]);

  useEffect(() => {
    const results = assessments.filter(exam =>
      exam.assessment_type_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exam.term_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAssessments(results);
  }, [searchTerm, assessments]);

  if (!auth) return null;

  return (
    <div className="dashboard-content">
      <ToastContainer />
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="fw-bold text-dark mb-1 d-flex align-items-center">
            <FaClipboardList className="me-3 text-primary" />
            Qiimeynta Firfircoon
          </h2>
          {activeTerm && <p className="text-secondary mb-0 fw-medium">Liiska imtixaanada: <span className="text-primary">{activeTerm.name} ({activeTerm.academic_year_name})</span></p>}
        </div>
        <div className="d-flex gap-2">
          <Link to="/exam-configuration" className="btn btn-outline-primary d-flex align-items-center">
            <FaList className="me-2" /> Habeynta
          </Link>
          <Link to="/enter-marks" className="btn btn-primary d-flex align-items-center shadow-sm">
            <FaPlus className="me-2" /> Geli Dhibcaha
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : (
        <>
          {/* Search Bar */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body p-2">
              <div className="input-group">
                <span className="input-group-text bg-white border-0 ps-3"><FaSearch className="text-muted" /></span>
                <input
                  type="text"
                  className="form-control border-0 py-2"
                  placeholder="Raadi qiimeynta..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="row g-4">
            {filteredAssessments.length > 0 ? (
              filteredAssessments.map((exam) => (
                <div key={exam.id} className="col-md-6 col-lg-4">
                  <div className="card h-100 border-0 shadow-sm hover-shadow transition-all">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill fw-bold">
                          {exam.term_name}
                        </span>
                        <div className="dropdown">
                          {/* Actions could go here */}
                        </div>
                      </div>

                      <h4 className="fw-bold text-dark mb-2">{exam.assessment_type_name}</h4>

                      <div className="d-flex align-items-center text-secondary mb-3">
                        <FaClipboardList className="me-2" />
                        <span className="small fw-medium">Miisaanka: {exam.weight}% | Max: {exam.max_marks}</span>
                      </div>

                      <div className="progress" style={{ height: '6px' }}>
                        <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${exam.weight}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-5">
                <div className="display-1 text-muted mb-3 opacity-25"><FaClipboardList /></div>
                <h4 className="text-secondary">Lama helin qiimeyn</h4>
                <p className="text-muted">Aad qeybta habeynta si aad ugu darto qiimeyn teermkan.</p>
                <Link to="/exam-configuration" className="btn btn-outline-primary mt-2">
                  Aad Habeynta
                </Link>
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        .hover-shadow:hover { 
            transform: translateY(-5px); 
            box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important;
        }
        .transition-all { transition: all 0.3s ease; }
      `}</style>
    </div>
  );
};

export default ShowExams;